import { estimateLoadingState } from "@/atoms/parlors/estimateState";
import { DetailPriceProps } from "@/components/Parlor/Funeral/DetailPrice";
import DetailSkeleton from "@/components/SkeletonLoader/DetailSkeleton";
import useParlorDetail from "@/hooks/useParlorDetailByType";
import { Box, Button, Divider, Flex, HStack, VStack } from "@chakra-ui/react";
import styled from "@emotion/styled";
import { useVariation } from "@hackler/react-sdk";
import {
  call,
  coerceCssPixelValue,
  ColorIcon,
  colors,
  CSSPixelValue,
  customEvent,
  LargeButton,
  LARGEL_BUTTON_TYPE,
  useResponsive,
} from "goi_common";
import { useRouter } from "next/router";
import { Fragment, useEffect, useMemo } from "react";
import { useSetRecoilState } from "recoil";

function ParlorEstimatesContainer({
  parlorId,
  expected_charge_sum,
}: {
  parlorId: string;
  expected_charge_sum: any;
}) {
  const setEstimateLoading = useSetRecoilState(estimateLoadingState);
  const { parlor, parlorRevalidate } = useParlorDetail({ parlorId, type: "가족장" });
  const router = useRouter();
  const variation = useVariation(118);
  const { results } = useMemo<any>(() => {
    if (parlor) return parlor;
    else return {};
  }, [parlor]);

  useEffect(() => {
    parlorRevalidate().then(() => {
      setEstimateLoading(false);
    });
  }, [parlorRevalidate, setEstimateLoading, parlorId]);

  if (!parlor)
    return (
      <VStack width="100%" alignItems="center">
        <VStack maxWidth="480px" width="100%">
          <DetailSkeleton />
        </VStack>
      </VStack>
    );

  return (
    <VStack width="100%" alignItems="center">
      <VStack maxWidth="480px" width="100%" gap={0}>
        <VStack padding="12px 16px 6px" width="100%" alignItems="start" gap={0}>
          <VStack zIndex={2} width="100%" alignItems="start">
            <div className="head_24_b orange650">{parlor.name}</div>
          </VStack>
        </VStack>

        <Box width="100%" px="12px" pb="12px">
          <LargeButton
            buttonType={LARGEL_BUTTON_TYPE.ORANGE}
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "parlor_estimates",
                  trigger_and_target: "top-uv_mtch_rqst",
                },
              });
              router.push(
                variation === "A" ? "/match/request/" : `/match/parlor-requests/${parlorId}/`,
              );
            }}
            height={44}
          >
            <HStack className="button_14_sb">
              <span>상황에 맞는 맞춤형 상세 비용 알아보기</span>
              <ColorIcon name="arrow_right_small" color={colors.white} size={14} />
            </HStack>
          </LargeButton>
        </Box>
        <VStack width="100%" padding="6px 0 16px" background={colors.warmGray100} gap={2}>
          <Box
            className="subtitle_18_b warmGray800"
            width="100%"
            textAlign="center"
            padding="6px 16px 0"
          >
            전국 장례식장 기준 평균 장례 비용
          </Box>
          <VStack gap={0} width="300px" px="36px">
            <HStack width="100%" alignItems="end" justifyContent="space-between">
              <span className="gray500 body_14_m">(2024년 기준) </span>
              <span className="black head_24_b">약 1,060만원</span>
            </HStack>
          </VStack>
        </VStack>

        <VStack
          width="100%"
          padding="0 12px"
          className="caption_11_m gray500"
          alignItems="start"
          justifyContent="end"
          gap={0}
        >
          <Box>
            *
            <u>
              <a
                target="_blank"
                href="https://www.kca.go.kr/smartconsumer/sub.do?menukey=7301&mode=view&no=1001837025&page=11&cate=00000057"
              >
                한국 소비자원(2015)
              </a>
            </u>
            기준 물가 상승률 반영
            {/* <br />
             *장례식장 음식, 시설 비용 약 <b>1,060만원</b> 포함 */}
          </Box>
        </VStack>
        <Box paddingTop="0px" width="100%" textAlign="center" className="head_24_b orange700">
          VS
        </Box>

        <VStack width="100%" padding="6px 0 16px" background={colors.warmGray100} gap={2}>
          <Box
            className="subtitle_18_b warmGray800"
            width="100%"
            textAlign="center"
            padding="6px 16px 0"
          >
            {parlor.name} 예상 상세 비용
          </Box>
          <VStack gap={0} width="300px">
            <HStack width="100%" alignItems="center" justifyContent="center">
              <span className="black subtitle_21_b">
                {expected_charge_sum["무빈소"].toLocaleString()}원 ~
                {expected_charge_sum["일반장"].toLocaleString()}원
              </span>
            </HStack>
          </VStack>
        </VStack>
        <VStack
          width="100%"
          padding="0 12px"
          className="caption_11_m gray500"
          alignItems="start"
          justifyContent="end"
          gap={0}
        >
          <Box width="100%">
            * 공공정보를 바탕으로 {parlor.name} 예상 비용을 안내드립니다. 산출된 비용은 예상
            비용으로 반드시 사전 상담을 통하여 확인해야합니다.
          </Box>
        </VStack>

        <VStack
          className="subtitle_21_b"
          width="100%"
          textAlign="start"
          padding="16px 16px"
          alignItems="start"
          gap={2}
        >
          {/* <Box p="0 10px 12px" width="100%" className="caption_12_b ">
            <NotificationBox>
              상세 비용, 할인 가능 여부를 상세 견적/상담을 통해 맞춤형으로 안내드립니다.
            </NotificationBox>
          </Box> */}
          <Box width="100%" position="relative">
            <Box
              position="absolute"
              bottom="0"
              background="linear-gradient(to bottom, #ffffff99 0%, rgba(255, 255, 255, 1) 100%);"
              width="100%"
              height="360px"
            ></Box>

            <Box width="100%" pt="0" px="64px" pb="12px" position="absolute" bottom="135px">
              <LargeButton
                buttonType={LARGEL_BUTTON_TYPE.WARMGRAY700}
                onClick={() => {
                  customEvent({
                    newGtm: {
                      path: "parlor_estimates",
                      trigger_and_target: "bottom-uv_mtch_rqst",
                    },
                  });
                  router.push(
                    variation === "A" ? "/match/request/" : `/match/parlor-requests/${parlorId}/`,
                  );
                }}
                height={44}
              >
                <HStack className="button_14_sb">
                  <span>상세 비용 확인</span>
                  <ColorIcon name="arrow_right_small" color={colors.white} size={14} />
                </HStack>
              </LargeButton>
            </Box>
            <DetailPrice results={results} variation={variation} parlorId={parlorId} />
          </Box>
        </VStack>
        <Box height="60px" />
      </VStack>
      <FixedButtons variation={variation} parlorId={parlorId} />
    </VStack>
  );
}

export default ParlorEstimatesContainer;

// const PriceCell = ({ name, value }: { name: string; value: string }) => {
//   return (
//     <HStack
//       width="100%"
//       className="body_14_m gray600"
//       justifyContent="space-between"
//       alignItems="end"
//       padding="0 16px"
//     >
//       <Box>{name}</Box>
//       <Box className="subtitle_16_b black" textAlign="right">
//         {value}
//       </Box>
//     </HStack>
//   );
// };

function DetailPrice({
  isNotLocale,
  results,
  variation,
  parlorId,
}: DetailPriceProps & { variation: string; parlorId: string }) {
  return (
    <Flex flexDirection="column">
      <div style={{ marginBottom: "8px" }} />
      {Object.entries(results).map(([title, { sum, results }]) => (
        <Fragment key={title}>
          <DetailItem
            variation={variation}
            title={title}
            sum={sum}
            isNotLocale={isNotLocale}
            results={results}
            parlorId={parlorId}
          />
        </Fragment>
      ))}
    </Flex>
  );
}

export interface DetailItemProps {
  title: string;
  sum: number | undefined;
  isNotLocale?: boolean;
  results: {
    [key: string]: {
      detail?: string;
      charge?: number | string;
      has_alpha?: boolean;
    };
  };
  padding?: CSSPixelValue;
}

function DetailItem({
  title,
  sum,
  isNotLocale = false,
  results,
  padding = "12px 20px",
  variation,
  parlorId,
}: DetailItemProps & { variation: string; parlorId: string }) {
  // const parsed = useMemo(() => {
  //   return results;
  // }, [results]);
  const router = useRouter();
  const parsedSum = useMemo(() => {
    if (sum) {
      const lowerBound = Math.floor(sum / 100000) * 100000;
      // 상한값 설정
      let variation = 500000;
      if (title === "시설") {
        variation = 800_000;
      } else if (title === "음식") {
        variation = 1_000_000;
      } else if (title === "기타") {
        variation = 9_00_000;
      }
      const upperBound = lowerBound + variation;

      return `${lowerBound.toLocaleString()} 원 ~ ${upperBound.toLocaleString()} 원`;
    } else {
      return "상세 상담 필요";
    }
  }, [sum]);
  return (
    <DetailItemRoot style={{ padding: coerceCssPixelValue(padding) }}>
      <Flex className="body_14_m" justifyContent="space-between">
        <HStack>
          <Box
            className="caption_12_b white"
            background={colors.green400}
            padding="4px 8px"
            borderRadius="24px"
          >
            장례식장
          </Box>
          <div>{title}</div>
        </HStack>
        <div>{parsedSum}</div>
      </Flex>
      <div style={{ marginBottom: "8px" }} />
      <Divider color="var(--gray400)" />
      {Object.entries(results).map(([key, { detail, charge, has_alpha }]) => (
        <Fragment key={key}>
          <div style={{ marginBottom: "12px" }} />
          <Flex className="body3_r" justifyContent="space-between">
            <div>{key}</div>
            <Flex alignItems="center">
              {detail && (
                <>
                  <span>{detail}</span>
                </>
              )}
            </Flex>
          </Flex>
          {key === "빈소, 접객실 사용료" && (
            <VStack
              width="100%"
              className="body_14_m"
              background={colors.gray50}
              marginTop="12px"
              padding="16px 16px"
            >
              <Box width="100%" textAlign="start" className="subtitle_14_b">
                {`"조문객이 100명이라고 100평 빈소를 선택했더니 빈소가 텅텅 비고 200만원 더
                지불했어요"`}
                <br />
                <br />
                {`"남은 빈소가 80평이라 일단 예약했는데 손님이 절반이 채 오지 않았어요"`}
                <br />
                <br />
                <Box width="100%" textAlign="start">
                  <span className="body_14_m">
                    - 빈소, 어떤 크기를 선택해야할까요?
                    <br />- 입식, 좌식, 어떤 형태가 좋나요?
                  </span>
                </Box>
              </Box>
              <br />
              <Button
                background={colors.warmGray400}
                color={colors.warmGray750}
                onClick={() => {
                  customEvent({
                    newGtm: {
                      path: "parlor_estimates",
                      trigger_and_target: "binso_content-see_details",
                    },
                  });
                  router.push(
                    variation === "A" ? "/match/request/" : `/match/parlor-requests/${parlorId}/`,
                  );
                }}
              >
                미리 알아보기
              </Button>
            </VStack>
          )}
        </Fragment>
      ))}
    </DetailItemRoot>
  );
}

const DetailItemRoot = styled.div`
  display: flex;
  flex-direction: column;

  :nth-of-type(2n) {
    background: var(--white);
  }

  :nth-of-type(2n + 1) {
    background: var(--gray50);
  }
`;

const FixedButtons = ({ variation, parlorId }: { variation: string; parlorId: string }) => {
  const { isBiggerThanMd } = useResponsive();
  const router = useRouter();
  return (
    <VStack
      alignItems="center"
      width="100%"
      position="fixed"
      height="max-content"
      style={{
        bottom: isBiggerThanMd ? 0 : 0,
      }}
    >
      <VStack width="100%" alignItems="center">
        <VStack width="100%" background={colors.gray50} alignItems="center">
          <HStack
            width="100%"
            maxWidth="480px"
            height={"64px"}
            gap="0"
            css={{
              margin: 0,
              height: "64px",
              bottom: isBiggerThanMd ? 0 : 0,
              zIndex: 5,
            }}
          >
            <HStack width="100%" height="100%" padding={"4px 20px 12px"} gap={0}>
              <Button
                style={{
                  color: "white",
                  width: "100%",
                  height: "100%",
                  backgroundColor: colors.black,
                  borderRadius: "5px",
                }}
                className="button"
                onClick={() => {
                  customEvent({
                    newGtm: {
                      path: "parlor_estimates",
                      trigger_and_target: "fixed_uv_mtch_rqst",
                    },
                  });

                  router.push(
                    variation === "A" ? "/match/request/" : `/match/parlor-requests/${parlorId}/`,
                  );
                }}
              >
                <VStack width="100%">
                  <div className="caption1">이어서</div>
                  <HStack
                    marginTop="0!important"
                    className="button_14_sb"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <div>상세 비용 알아보기</div>
                    <ColorIcon name="icon-arrow-right-circle-mono" color={colors.white} size={16} />
                  </HStack>
                </VStack>
              </Button>
              <Button
                style={{
                  color: "white",
                  width: "100%",
                  height: "100%",
                  backgroundColor: colors.black,
                  borderRadius: "5px",
                }}
                onClick={() => {
                  customEvent({
                    newGtm: {
                      path: "parlor_estimates",
                      trigger_and_target: "fixed_call",
                    },
                  });
                  call("1666-9784");
                  // onQuickCallModal();
                }}
                className="button"
              >
                <VStack alignItems="center" gap={0} justifyContent="center">
                  <div className="caption1">24시간 전화상담</div>
                  <HStack
                    className="button_14_sb"
                    alignItems="center"
                    justifyContent="center"
                    mt="0!important"
                  >
                    <div>임종접수/빈소문의</div>
                    <ColorIcon name="icon-call-mono" size={16} color="white" />
                  </HStack>
                </VStack>
              </Button>
            </HStack>
          </HStack>
        </VStack>
      </VStack>
    </VStack>
  );
};
